import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import PosShell from "@/components/pos/PosShell";
import Keypad from "@/components/pos/Keypad";
import PaymentSheet from "@/components/pos/PaymentSheet";
import { formatINR, itemAmount, itemsTotal, posApi, roundMoney } from "@/lib/pos";
import QuickPrices, { loadQuickPrices } from "@/components/pos/QuickPrices";
import { useAuth } from "@/context/AuthContext";

export default function PosCalculator() {
  const { token } = useAuth();
  const [entry, setEntry] = useState("");
  const [qty, setQty] = useState("");
  const [mode, setMode] = useState("price"); // price | qty
  const [label, setLabel] = useState("");
  const [items, setItems] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [todayTotal, setTodayTotal] = useState(null);
  const [quickPrices, setQuickPrices] = useState(loadQuickPrices);

  const loadToday = useCallback(async () => {
    if (!token) return;
    try {
      const s = await posApi.todaySummary(token);
      setTodayTotal(s.total_sales);
    } catch (_e) {
      /* header total is non-critical */
    }
  }, [token]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  const total = itemsTotal(items);
  const pending = entry === "" ? 0 : roundMoney(entry) * (qty === "" ? 1 : Number(qty));

  const onKey = (k) => {
    if (mode === "qty") {
      setQty((q) => (q + (k.length > 1 ? "0" : k)).replace(/^0+/, "").slice(0, 3));
      return;
    }
    setEntry((e) => (e + k).replace(/^0+(?=\d)/, "").slice(0, 8));
  };

  const resetEntry = () => {
    setEntry("");
    setQty("");
    setMode("price");
    setLabel("");
  };

  const commit = (sign) => {
    const price = roundMoney(entry);
    if (!entry || price <= 0) {
      toast.error("Enter an amount first");
      return;
    }
    setItems((prev) => [
      ...prev,
      { price, qty: qty === "" ? 1 : Math.max(1, Number(qty)), label: label.trim(), sign },
    ]);
    resetEntry();
  };

  const onBackspace = () => {
    if (mode === "qty") {
      if (qty === "") setMode("price");
      else setQty((q) => q.slice(0, -1));
      return;
    }
    setEntry((e) => e.slice(0, -1));
  };

  const clearAll = () => {
    setItems([]);
    resetEntry();
  };

  return (
    <PosShell todayTotal={todayTotal}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Running list */}
        <section className="order-2 lg:order-1 rounded-[4px] bg-white border-2 border-slate-200 flex flex-col">
          <div className="px-4 py-3 border-b-2 border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-black text-slate-900 uppercase tracking-[0.08em]">
              Items ({items.length})
            </h2>
            {items.length > 0 && (
              <button
                type="button"
                data-testid="clear-items-button"
                onClick={clearAll}
                className="inline-flex items-center gap-1.5 text-[#b91c1c] font-bold text-xs uppercase tracking-[0.12em]"
              >
                <Trash2 size={14} /> Clear
              </button>
            )}
          </div>

          <div
            data-testid="items-list"
            className="flex-1 max-h-[46vh] lg:max-h-[52vh] overflow-y-auto divide-y divide-slate-100"
          >
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-400 font-semibold text-sm">
                Type a price and tap + to add it here
              </div>
            ) : (
              items.map((it, i) => (
                <div
                  key={i}
                  data-testid={`item-row-${i}`}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      {it.label || `Item ${i + 1}`}
                    </div>
                    <div className="text-xs font-semibold text-slate-500 tabular-nums">
                      ₹{it.price}
                      {it.qty > 1 ? ` × ${it.qty}` : ""}
                      {it.sign < 0 ? " · discount" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`font-black text-xl tabular-nums ${
                        it.sign < 0 ? "text-[#b91c1c]" : "text-slate-900"
                      }`}
                    >
                      {formatINR(itemAmount(it))}
                    </span>
                    <button
                      type="button"
                      data-testid={`remove-item-${i}`}
                      onClick={() => setItems((p) => p.filter((_, x) => x !== i))}
                      className="w-8 h-8 rounded-[4px] bg-slate-100 hover:bg-[#fee2e2] hover:text-[#b91c1c] text-slate-500 flex items-center justify-center"
                      aria-label="Remove item"
                    >
                      <X size={15} strokeWidth={3} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-4 bg-slate-900 text-white flex items-end justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">
              Running total
            </div>
            <div
              data-testid="running-total"
              className="font-black text-3xl leading-none tabular-nums"
            >
              {formatINR(total)}
            </div>
          </div>
        </section>

        {/* Keypad */}
        <section className="order-1 lg:order-2 space-y-3">
          <div className="rounded-[4px] bg-white border-2 border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                {mode === "qty" ? "Quantity" : "Price"}
              </span>
              {qty !== "" && (
                <span className="text-xs font-bold text-[#0e6ea6] tabular-nums">
                  {entry || 0} × {qty} = ₹{pending}
                </span>
              )}
            </div>
            <div
              data-testid="current-entry"
              className="mt-1 font-black text-5xl text-slate-900 tabular-nums leading-none"
            >
              ₹{entry === "" ? "0" : entry}
              {mode === "qty" && (
                <span className="text-[#0e6ea6]"> × {qty === "" ? "" : qty}</span>
              )}
            </div>
            <input
              data-testid="item-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Item name (optional)"
              className="mt-3 w-full h-11 px-3 rounded-[4px] border-2 border-slate-200 focus:border-[#299fe0] outline-none font-semibold text-slate-800 text-sm"
            />
          </div>

          <QuickPrices
            prices={quickPrices}
            setPrices={setQuickPrices}
            onPick={(p) =>
              setItems((prev) => [...prev, { price: p, qty: 1, label: "", sign: 1 }])
            }
          />

          <Keypad
            onKey={onKey}
            onAdd={() => commit(1)}
            onSubtract={() => commit(-1)}
            onTimes={() => {
              if (!entry) {
                toast.error("Enter a price first");
                return;
              }
              setMode("qty");
              setQty("");
            }}
            onBackspace={onBackspace}
            onClear={clearAll}
          />

          <button
            type="button"
            data-testid="checkout-button"
            onClick={() => {
              if (items.length === 0) {
                toast.error("Add items before checkout");
                return;
              }
              setPayOpen(true);
            }}
            className="w-full h-[68px] rounded-[4px] bg-[#cb05a5] hover:bg-[#a3047e] text-white font-black text-xl uppercase tracking-[0.08em] shadow-lg transition-colors"
          >
            Generate receipt · {formatINR(total)}
          </button>
        </section>
      </div>

      <PaymentSheet
        open={payOpen}
        onClose={() => {
          setPayOpen(false);
          clearAll();
          loadToday();
        }}
        items={items}
        total={total}
        onCompleted={loadToday}
      />
    </PosShell>
  );
}
